import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import api from '../config/api.js';

const DATA_TYPES = [
  { value: 'carrier', labelKey: 'dataManagement.carriers' },
  { value: 'product', labelKey: 'dataManagement.products' },
  { value: 'lead_type', labelKey: 'dataManagement.leadTypes' },
  { value: 'agency', labelKey: 'dataManagement.agencies' },
];

const DataManagement = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [uniqueValues, setUniqueValues] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [canonicalName, setCanonicalName] = useState('');
  const [editingMapping, setEditingMapping] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const currentType = DATA_TYPES[activeTab].value;

  useEffect(() => {
    fetchData();
    setSearchQuery('');
    setSelectedValues([]);
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [valuesRes, mappingsRes] = await Promise.all([
        api.get(`/data-mappings/unique-values/${currentType}`),
        api.get(`/data-mappings?type=${currentType}`)
      ]);
      setUniqueValues(valuesRes.data);
      setMappings(mappingsRes.data);
    } catch (error) {
      showMessage(t('dataManagement.errorFetching'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, severity = 'success') => {
    setMessage({ text, severity });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSelectValue = (value) => {
    setSelectedValues(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleOpenDialog = () => {
    if (selectedValues.length === 0) {
      showMessage(t('dataManagement.selectAtLeastOne'), 'warning');
      return;
    }
    // Auto-suggest the most common value as canonical name
    const mostCommon = [...selectedValues].sort((a, b) => {
      const aCount = uniqueValues.find(v => v.value === a)?.count || 0;
      const bCount = uniqueValues.find(v => v.value === b)?.count || 0;
      return bCount - aCount;
    })[0];
    setCanonicalName(mostCommon || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCanonicalName('');
    setEditingMapping(null);
  };

  const handleSaveMappings = async () => {
    if (!canonicalName.trim()) {
      showMessage(t('dataManagement.enterCanonicalName'), 'warning');
      return;
    }

    try {
      if (editingMapping) {
        // Update existing mapping
        await api.put(`/data-mappings/${editingMapping.id}`, {
          canonical_value: canonicalName
        });
        showMessage(t('dataManagement.mappingUpdated'));
      } else {
        // Create new mappings for all selected values
        const mappingsToCreate = selectedValues.map(value => ({
          type: currentType,
          original_value: value,
          canonical_value: canonicalName
        }));
        await api.post('/data-mappings/batch', { mappings: mappingsToCreate });
        showMessage(t('dataManagement.mappingsCreated', { count: selectedValues.length }));
      }

      setSelectedValues([]);
      handleCloseDialog();
      fetchData();
    } catch (error) {
      showMessage(t('dataManagement.errorSaving'), 'error');
    }
  };

  const handleEditMapping = (mapping) => {
    setEditingMapping(mapping);
    setCanonicalName(mapping.canonical_value);
    setDialogOpen(true);
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm(t('dataManagement.confirmDeleteMapping'))) {
      return;
    }

    try {
      await api.delete(`/data-mappings/${id}`);
      showMessage(t('dataManagement.mappingDeleted'));
      fetchData();
    } catch (error) {
      showMessage(t('dataManagement.errorDeleting'), 'error');
    }
  };

  // Group mappings by canonical value
  const groupedMappings = mappings.reduce((acc, mapping) => {
    if (!acc[mapping.canonical_value]) {
      acc[mapping.canonical_value] = [];
    }
    acc[mapping.canonical_value].push(mapping);
    return acc;
  }, {});

  // Get mapped value for a given original value
  const getMappedValue = (value) => {
    const mapping = mappings.find(m => m.original_value === value);
    return mapping ? mapping.canonical_value : null;
  };

  // Filter unique values based on search query
  const filteredValues = uniqueValues.filter(item =>
    item.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Select all filtered/searched values
  const handleSelectAllFromSearch = () => {
    const valuesToSelect = filteredValues.map(item => item.value);
    setSelectedValues(prev => {
      // Add all filtered values that aren't already selected
      const newSelections = valuesToSelect.filter(v => !prev.includes(v));
      return [...prev, ...newSelections];
    });
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedValues([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t('dataManagement.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('dataManagement.subtitle')}
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dataManagement.uniqueValues')}
              </Typography>
              <Typography variant="h4">{uniqueValues.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dataManagement.activeMappings')}
              </Typography>
              <Typography variant="h4">{mappings.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setSelectedValues([]);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {DATA_TYPES.map((type, index) => (
            <Tab key={type.value} label={t(type.labelKey)} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {selectedValues.length > 0
                  ? t('dataManagement.selected', { count: selectedValues.length })
                  : t('dataManagement.selectValuesToGroup')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CheckBoxIcon />}
                  onClick={handleSelectAllFromSearch}
                  disabled={filteredValues.length === 0}
                >
                  {t('dataManagement.selectAll', { count: filteredValues.length })}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDeselectAll}
                  disabled={selectedValues.length === 0}
                >
                  {t('dataManagement.deselectAll')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  disabled={selectedValues.length === 0}
                >
                  {t('dataManagement.createMapping')}
                </Button>
              </Box>
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder={t('dataManagement.searchPlaceholder', { type: t(DATA_TYPES[activeTab].labelKey).toLowerCase() })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Existing Mappings */}
          {Object.keys(groupedMappings).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('dataManagement.currentMappings')}
              </Typography>
              {Object.entries(groupedMappings).map(([canonical, items]) => (
                <Card key={canonical} sx={{ mb: 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {canonical}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {items.map(item => (
                            <Chip
                              key={item.id}
                              label={item.original_value}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                              onDelete={() => handleDeleteMapping(item.id)}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Unique Values Table */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            {searchQuery
              ? t('dataManagement.allValuesFiltered', { filtered: filteredValues.length, total: uniqueValues.length })
              : t('dataManagement.allValues')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">{t('dataManagement.select')}</TableCell>
                  <TableCell>{t('dataManagement.originalValue')}</TableCell>
                  <TableCell align="right">{t('common.count')}</TableCell>
                  <TableCell>{t('dataManagement.mappedTo')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredValues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {searchQuery ? t('dataManagement.noValuesMatch') : t('dataManagement.noValuesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredValues.map((item, index) => {
                    const mappedValue = getMappedValue(item.value);
                    const hasVariations = item.variations && item.variations.length > 1;
                    return (
                      <TableRow key={index} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedValues.includes(item.value)}
                            onChange={() => handleSelectValue(item.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.value}
                            {hasVariations && (
                              <Chip
                                label={t('dataManagement.variations', { count: item.variations.length })}
                                size="small"
                                variant="outlined"
                                color="info"
                                title={item.variations.map(v => `${v.value} (${v.count})`).join(', ')}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.count}</TableCell>
                        <TableCell>
                          {mappedValue ? (
                            <Chip label={mappedValue} size="small" color="primary" />
                          ) : hasVariations ? (
                            <Chip label={t('dataManagement.autoGrouped')} size="small" color="success" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t('dataManagement.notMapped')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Create/Edit Mapping Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMapping ? t('dataManagement.editMapping') : t('dataManagement.createMapping')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {!editingMapping && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t('dataManagement.selectedValues')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {selectedValues.map(value => (
                    <Chip key={value} label={value} size="small" sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>
              </>
            )}

            <TextField
              fullWidth
              label={t('dataManagement.canonicalName')}
              value={canonicalName}
              onChange={(e) => setCanonicalName(e.target.value)}
              helperText={t('dataManagement.canonicalHelperText')}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveMappings} variant="contained" startIcon={<SaveIcon />}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement;
